import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faUsers,
  faUser,
  faTimes,
  faBuilding,
  faRefresh,
  faClipboardCheck,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import { Check, X, Clock, Calendar, MessageCircle, UserCheck, UserX } from 'lucide-react';
import StudentSelectionModal from '../components/CompanyProcess/StudentSelectionModal'; // Import the new modal
import '../styles/MatchedStudents.css';

const MatchedStudents = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [matchedStudents, setMatchedStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const recordsPerPage = 10;

  // Get data from navigation state - with fallbacks and debug
  const {
    companyName,
    qualification,
    skillKnown, // Changed from skillSet to knownSkill
    salary,
    experienceRequired,
    dateOfPlacement, // Added interview date
    recordId,
    placementRecord
  } = location.state || {};

  // Debug: Log the received data
  console.log('Navigation State Data:', {
    companyName,
    qualification,
    skillKnown,
    salary,
    experienceRequired,
    dateOfPlacement,
    recordId,
    placementRecord
  });

  useEffect(() => {
    if (recordId) {
      fetchMatchedStudents();
    } else {
      setError('No company record ID provided');
      setIsLoading(false);
    }
  }, [recordId]);

  const fetchMatchedStudents = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');

      if (!token) {
        setError('Authentication token not found. Please login again.');
        setIsLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/placement/${recordId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Fetched matched students:', result);
        console.log('Full API Response:', result); // Debug log
        console.log('Location state data:', location.state); // Debug log

        const studentsData = result.data || [];
        setMatchedStudents(studentsData);
      } else {
        const errorData = await response.json();

        if (response.status === 401) {
          setError('Authentication failed. Please login again.');
        } else if (response.status === 404) {
          setError('No matched students found for this company.');
        } else {
          setError(errorData.message || 'Failed to fetch matched students.');
        }
      }
    } catch (error) {
      console.error('Error fetching matched students:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Not Set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Check if interview is today
  const isInterviewToday = (interviewDate) => {
    if (!interviewDate) return false;
    const today = new Date().toISOString().split('T')[0];
    const interview = new Date(interviewDate).toISOString().split('T')[0];
    return today === interview;
  };

  // Get comprehensive status for a student (similar to notifications page)
  const getStudentStatus = (student) => {
    // If offer was rejected by student
    if (student.isAccepted === false) {
      return {
        type: 'rejected',
        text: 'Rejected',
        icon: <X size={12} />,
        reason: student.rejectReason,
        showDetails: true
      };
    }

    // If student has selection status (after attending interview)
    if (student.attended === true && student.isSelected !== null) {
      if (student.isSelected === true) {
        return {
          type: 'selected-by-company',
          text: 'Selected by Company',
          icon: <UserCheck size={12} />,
          feedback: student.selectionFeedback,
          showDetails: true
        };
      } else {
        return {
          type: 'not-selected-by-company',
          text: 'Not Selected by Company',
          icon: <UserX size={12} />,
          feedback: student.selectionFeedback,
          showDetails: true
        };
      }
    }

    // If student was selected by company (old logic - keeping for backward compatibility)
    if (student.isSelected && student.isSelected !== null) {
      return {
        type: 'selected',
        text: 'Selected',
        icon: <Check size={12} />,
        showDetails: false
      };
    }

    // If offer was accepted by student
    if (student.isAccepted === true) {
      // Check if attendance has been marked
      if (student.attended !== null && student.attended !== undefined) {
        if (student.attended) {
          return {
            type: 'attended',
            text: 'Attended',
            icon: <Check size={12} />,
            feedback: student.feedback,
            showDetails: true,
            showEditButton: student.isSelected === null // Show edit button if selection not decided
          };
        } else {
          return {
            type: 'not-attended',
            text: 'Not Attended',
            icon: <X size={12} />,
            feedback: student.feedback,
            showDetails: true
          };
        }
      }

      // Check if interview is today and attendance is pending
      if (isInterviewToday(dateOfPlacement)) {
        return {
          type: 'pending-attendance',
          text: 'Pending Attendance',
          icon: <Clock size={12} />,
          showDetails: false
        };
      }

      // Accepted and waiting for interview
      return {
        type: 'accepted-waiting',
        text: 'Accepted - Waiting for Interview',
        icon: <Calendar size={12} />,
        showDetails: false
      };
    }

    // Pending response (null or undefined)
    return {
      type: 'pending',
      text: 'Assigned',
      icon: <FontAwesomeIcon icon={faClipboardCheck} style={{ fontSize: '12px' }} />,
      showDetails: false
    };
  };

  // Handle opening the selection modal
  const handleEditSelection = (student) => {
    setSelectedStudent(student);
    setIsModalOpen(true);
  };

  // Handle closing the modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStudent(null);
  };

  // Handle selection update
  const handleSelectionUpdate = (studentId, selectionData) => {
    setMatchedStudents(prevStudents => 
      prevStudents.map(student => 
        student.studentId === studentId 
          ? {
              ...student,
              isSelected: selectionData.isSelected,
              selectionFeedback: selectionData.selectionFeedback,
              selectionUpdatedAt: new Date().toISOString()
            }
          : student
      )
    );
  };

  // Updated getCurrentStatus function using the comprehensive logic
  const getCurrentStatus = (student) => {
    const status = getStudentStatus(student);
    
    return (
      <div className="status-cell-container">
        <span className={`status-badge ${status.type}`}>
          {status.icon}
          {status.text}
        </span>

        {/* Show rejection reason if rejected */}
        {status.type === 'rejected' && status.reason && status.showDetails && (
          <div className="status-details">
            <span className="detail-label">Reason: </span>
            <span className="detail-text">{status.reason}</span>
          </div>
        )}

        {/* Show interview feedback if provided */}
        {(status.type === 'attended' || status.type === 'not-attended') && status.feedback && status.showDetails && (
          <div className="status-details">
            <MessageCircle size={12} />
            <span className="detail-text" title={status.feedback}>
              Interview Feedback: {status.feedback.length > 50 ?
                `${status.feedback.substring(0, 50)}...` :
                status.feedback
              }
            </span>
          </div>
        )}

        {/* Show selection feedback if provided */}
        {(status.type === 'selected-by-company' || status.type === 'not-selected-by-company') && status.feedback && status.showDetails && (
          <div className="status-details">
            <MessageCircle size={12} />
            <span className="detail-text" title={status.feedback}>
              Selection Feedback: {status.feedback.length > 50 ?
                `${status.feedback.substring(0, 50)}...` :
                status.feedback
              }
            </span>
          </div>
        )}

        {/* Show edit button for attended students without selection decision */}
        {status.showEditButton && (
          <div className="edit-selection-container">
            <button
              className="edit-selection-btn"
              onClick={() => handleEditSelection(student)}
              title="Update Selection Status"
            >
              <FontAwesomeIcon icon={faEdit} />
              Edit Selection
            </button>
          </div>
        )}
      </div>
    );
  };

  // Pagination calculations
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = matchedStudents.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(matchedStudents.length / recordsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="matched-students-wrapper">
        <div className="matched-students-card">
          <div className="loading-state">
            <FontAwesomeIcon icon={faRefresh} spin size="2x" />
            <h3>Loading Matched Students...</h3>
            <p>Please wait while we fetch the data</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="matched-students-wrapper">
        <div className="matched-students-card">
          <div className="error-state">
            <FontAwesomeIcon icon={faTimes} size="2x" />
            <h3>Error</h3>
            <p>{error}</p>
            <div className="error-actions">
              <button className="retryms-button" onClick={fetchMatchedStudents}>
                Try Again
              </button>
              <button className="backms-button" onClick={handleGoBack}>
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="matched-students-wrapper">
      <div className="matched-students-container">
        {/* Header Section */}
        <div className="matched-students-header">
          <div className="header-left">
            <h2>
              <FontAwesomeIcon icon={faUsers} />
              Matched Students
            </h2>
          </div>
          <div className="header-right">
            <button className="refresh-button" onClick={handleGoBack}>
              <FontAwesomeIcon icon={faArrowLeft} />
              Back
            </button>
          </div>
        </div>

        {/* Company Info Card */}
        <div className="company-info-card">
          <div className="company-info-header">
            <FontAwesomeIcon icon={faBuilding} />
            <h3>Company Details</h3>
          </div>
          <div className="company-info-grid">
            <div className="infoms-item">
              <strong>Company Name:</strong>
              <span>{companyName || 'N/A'}</span>
            </div>

            <div className="infoms-item">
              <strong>Qualification Required:</strong>
              <span>{qualification || 'N/A'}</span>
            </div>
            <div className="infoms-item">
              <strong>Known Skills:</strong>
              <span>{skillKnown || 'N/A'}</span>
            </div>
            <div className="infoms-item">
              <strong>Salary Offered:</strong>
              <span>{salary || 'N/A'}</span>
            </div>
            <div className="infoms-item">
              <strong>Experience Required:</strong>
              <span>{experienceRequired || 'N/A'}</span>
            </div>
            <div className="infoms-item">
              <strong>Interview Date:</strong>
              <div className="interview-date-box">
                <span>{dateOfPlacement || 'N/A'}</span>
              </div>
            </div>
            <div className="infoms-item">
              <strong>Total Matched:</strong>
              <span>{matchedStudents.length}</span>
            </div>
          </div>
        </div>

        {/* Students Table */}
        {matchedStudents.length > 0 ? (
          <div className="students-table-wrapper">
            <div className="table-container">
              <table className="students-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Student ID</th>
                    <th>Student Name</th>
                    <th>Branch</th>
                    <th>Current Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRecords.map((student, index) => (
                    <tr key={student.id} className="student-row">
                      <td>{indexOfFirstRecord + index + 1}</td>
                      <td className="student-id-cell">
                        <FontAwesomeIcon icon={faUser} />
                        {student.studentId}
                      </td>
                      <td>{student.studentName}</td>
                      <td>{student.branch}</td>
                      <td className="status-cell">
                        {getCurrentStatus(student)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <button
                  onClick={handlePrevious}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  «
                </button>

                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}

                <button
                  onClick={handleNext}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  »
                </button>
              </div>
            )}

            {/* Summary */}
            <div className="table-summary">
              <p>
                Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, matchedStudents.length)} of {matchedStudents.length} students
              </p>
            </div>
          </div>
        ) : (
          <div className="no-students-card">
            <FontAwesomeIcon icon={faUser} size="3x" />
            <h3>No Matched Students</h3>
            <p>No students have been matched to this company yet.</p>
          </div>
        )}

        {/* Selection Modal */}
        <StudentSelectionModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          student={selectedStudent}
          companyId={recordId}
          onSelectionUpdate={handleSelectionUpdate}
        />
      </div>
    </div>
  );
};

export default MatchedStudents;